import React from "react";
import type { Localizer } from "@/main/react/Types";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";
import type { EndorsementKind } from "@/main/common/EndorsementTypes";

/** E5: a compliment is one sentence. Mirrors `EndorsementService.NOTE_MAX_LENGTH`. */
export const ENDORSEMENT_NOTE_MAX_LENGTH = 280;

// One prompt per kind, and the prompt is the whole reason the note is cheap (§8.3): a giver who is
// asked "What moved you?" writes a sentence, a giver who is shown an empty box writes nothing. Kept
// to a short second-person question in every locale.
const PROMPT_KEY: Record<EndorsementKind, LocalizationKey> = {
    TAUGHT_ME: "discussion.endorsement.prompt.taught_me",
    FAIR_TO_THE_OTHER_SIDE: "discussion.endorsement.prompt.fair_to_the_other_side",
    WELL_SAID: "discussion.endorsement.prompt.well_said",
    MOVED_ME: "discussion.endorsement.prompt.moved_me",
    STAYED_WITH_ME: "discussion.endorsement.prompt.stayed_with_me",
    KEPT_ME_READING: "discussion.endorsement.prompt.kept_me_reading",
    PUT_IT_INTO_WORDS: "discussion.endorsement.prompt.put_it_into_words",
};

/**
 * The kind-scoped note composer that follows an endorsement — and Skip is free.
 *
 * <p>Modelled on `MindChangedComposer`, and it <b>diverges from it on the one property that matters
 * there</b>: this note is OPTIONAL. The act completed on the tap (E4); the kind has already told the
 * writer what the sentence is about, which is what makes it cheap to write, and a blank box is what
 * the group board proved gets nothing. Do not copy the "the sentence is required" note across.
 *
 * <p>Flat-X per rule 23: a bordered panel rather than a shadowed card, pill controls, one focus ring.
 *
 * <p>Design: `designs/00-inbox/2026-08-25-profile-endorsements-and-the-appreciation-wall-design.md`
 * §7 (E4, E5), §8.3.
 */
export function EndorsementComposer(props: {
    localizer: Localizer;
    kind: EndorsementKind;
    submitting?: boolean;
    onSubmit: (note: string) => void;
    onSkip: () => void;
}) {
    const { localizer, kind, submitting, onSubmit, onSkip } = props;
    const [text, setText] = React.useState("");
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Focus on open: a composer that appears without focus is a thing on the page rather than an
    // invitation, and on mobile it is what raises the keyboard.
    React.useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const trimmed = text.trim();
    const canSubmit = trimmed.length > 0 && !submitting;

    return (
        <div
            className="mt-3 rounded-xl border border-border bg-card p-3"
            data-playwright-id="thought-endorsement-composer"
            data-endorsement-kind={kind}
        >
            <p className="text-sm font-medium text-foreground">
                {localizer(PROMPT_KEY[kind])}
            </p>
            {/* Says what the note is (optional, short) rather than where it goes or how it is screened
                (rule 12). */}
            <p className="mt-0.5 text-xs text-neutral-text">
                {localizer("discussion.endorsement.note.optional")}
            </p>

            <textarea
                ref={textareaRef}
                value={text}
                onChange={(event) => setText(event.target.value.slice(0, ENDORSEMENT_NOTE_MAX_LENGTH))}
                rows={2}
                maxLength={ENDORSEMENT_NOTE_MAX_LENGTH}
                aria-label={localizer(PROMPT_KEY[kind])}
                data-playwright-id="thought-endorsement-composer-input"
                // 16px on a coarse pointer: iOS Safari auto-zooms any focused control under 16px.
                className="mt-2 w-full resize-y rounded-lg border border-border bg-input/70 px-3 py-2 text-sm [@media(pointer:coarse)]:text-base focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />

            <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    // Inert until there is something to post; never a validation message, because the
                    // note is optional and Skip is the honest exit.
                    disabled={!canSubmit}
                    onClick={() => onSubmit(trimmed)}
                    data-playwright-id="thought-endorsement-composer-submit"
                    className="inline-flex min-h-8 items-center rounded-full border border-foreground px-4 py-1.5 text-sm font-medium touch-manipulation [@media(pointer:coarse)]:min-h-11 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                    {localizer("discussion.endorsement.note.add")}
                </button>
                <button
                    type="button"
                    onClick={onSkip}
                    disabled={submitting}
                    data-playwright-id="thought-endorsement-composer-skip"
                    className="inline-flex min-h-8 items-center rounded-full border border-border px-4 py-1.5 text-sm touch-manipulation [@media(pointer:coarse)]:min-h-11 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                    {localizer("discussion.endorsement.note.skip")}
                </button>
                <span className="ml-auto text-xs tabular-nums text-neutral-text" aria-hidden="true">
                    {trimmed.length}/{ENDORSEMENT_NOTE_MAX_LENGTH}
                </span>
            </div>
        </div>
    );
}

export default EndorsementComposer;
